import type { Nullable } from '@src/shared/types/common';

export const localeCodeByKey: Record<LocalizableKey, string> = {
    Russian: 'ru-RU',
    English: 'en-US',
    Thai: 'th-TH',
    Turkish: 'tr-TR'
};

export const localizableKeys: readonly LocalizableKey[] = Object.keys(localeCodeByKey) as LocalizableKey[];

const localeKeyByCode = Object.fromEntries(
    Object.entries(localeCodeByKey).map(([key, code]) => [code, key as LocalizableKey])
) as Partial<Record<string, LocalizableKey>>;

export function getLocaleKeyByCode(localeCode: Nullable<string> | undefined): Nullable<LocalizableKey> {
    if (!localeCode) {
        return null;
    }

    return localeKeyByCode[localeCode] ?? null;
}

function getLocalizableControlElement(controlName: string, key: LocalizableKey): JQuery {
    const fieldNames = [`${controlName}.${key}`, `${controlName}_${key}`];
    const localizationField = $(`.localization-field[data-control-name="${controlName}"]`);
    const inputSelector = (name: string) => `textarea[name="${name}"], input[name="${name}"]`;

    for (const name of fieldNames) {
        const scoped = localizationField.find(inputSelector(name)).first();

        if (scoped.length) {
            return scoped;
        }
    }

    const idSelectors = fieldNames.map((name) => `#${name.replace(/\./g, '\\.')}`).join(', ');
    const $scopedById = localizationField.find(idSelectors);

    return $scopedById.length ? $scopedById.first() : $(idSelectors).first();
}

function getLocalizableControlValue(controlName: string, key: LocalizableKey): string {
    const $el = getLocalizableControlElement(controlName, key);

    if (!$el.length) {
        return '';
    }

    const editor = $el.getKendoEditor();

    if (editor) {
        return editor.value();
    }

    const textBox = $el.getKendoTextBox();

    if (textBox) {
        return textBox.value();
    }

    const textArea = $el.getKendoTextArea();

    if (textArea) {
        return textArea.value();
    }

    return ($el.val() ?? '') as string;
}

function setLocalizableControlValue(controlName: string, key: LocalizableKey, value: string): void {
    const $el = getLocalizableControlElement(controlName, key);

    if (!$el.length) {
        return;
    }

    const editor = $el.getKendoEditor();

    if (editor) {
        editor.value(value);
        return;
    }

    const textBox = $el.getKendoTextBox();

    if (textBox) {
        textBox.value(value);
        return;
    }

    const textArea = $el.getKendoTextArea();

    if (textArea) {
        textArea.value(value);
        return;
    }

    $el.val(value);
}

function getLocalizableString(controlName: string): LocalizableString {
    return localizableKeys.reduce((acc, key) => {
        acc[key] = getLocalizableControlValue(controlName, key);
        return acc;
    }, {} as LocalizableString);
}

function setLocalizableString(
    controlName: string,
    value: Nullable<PartialLocalizableString | undefined>
): void {
    localizableKeys.forEach((key) => {
        setLocalizableControlValue(controlName, key, value?.[key] ?? '');
    });
}

function makeLocalizableString(russian: string, english: string = '', thai: string = '', turkish: string = ''): LocalizableString {
    return { Russian: russian, English: english, Thai: thai, Turkish: turkish };
}

function changeLocalizableNameVisibility(controlName: string, countryId: Nullable<number> | undefined, excludedCountryId: number): void {
    const shouldShowAdditionalLocales = countryId !== excludedCountryId;
    const tabStrip = $(`#${controlName}_tsLocales`).data('kendoTabStrip');

    if (!tabStrip) {
        return;
    }

    localizableKeys.forEach((key) => {
        if (localeCodeByKey[key] === localeCodeByKey.Russian) {
            return;
        }

        tabStrip.tabGroup.find(`[data-locale="${localeCodeByKey[key]}"]`).toggle(shouldShowAdditionalLocales);

        if (!shouldShowAdditionalLocales) {
            setLocalizableControlValue(controlName, key, '');
        }
    });

    const selectedLocaleCode = tabStrip.select().attr('data-locale');

    if (!shouldShowAdditionalLocales && selectedLocaleCode && selectedLocaleCode !== localeCodeByKey.Russian) {
        tabStrip.select(tabStrip.tabGroup.find(`[data-locale="${localeCodeByKey.Russian}"]`));
    }
}

function setLocalizableLabelString(
    controlName: string,
    value: Nullable<PartialLocalizableString | undefined>,
    asText: boolean = true
): void {
    const set = (el: JQuery, v: string) => el[asText ? 'text' : 'html'](v);
    localizableKeys.forEach((key) => {
        set($(`#${controlName}${key}Label`), value?.[key] ?? '');
    });
}

function getLocalizableStringErrors(
    value: Nullable<PartialLocalizableString | undefined>,
    message: string,
    invalidValues?: string[]
): Nullable<string> {
    const errors: string[] = [];
    const isInvalid = (v?: string) => !v?.trim() || (invalidValues?.includes(v.trim()) ?? false);
    const localeSuffixes: Partial<Record<LocalizableKey, string>> = {
        English: 'en',
        Thai: 'th',
        Turkish: 'tr'
    };

    localizableKeys.forEach((key) => {
        if (!isInvalid(value?.[key])) {
            return;
        }

        const localeSuffix = localeSuffixes[key];
        errors.push(localeSuffix ? `${message} (${localeSuffix})` : message);
    });

    return errors.length ? errors.join('<br>') : null;
}

function getLocalizedValue(localizableString: Nullable<LocalizableString | undefined>): string {
    if (!localizableString) {
        return '';
    }

    const culture = kendo.culture().name;
    const key = getLocaleKeyByCode(culture) ?? 'Russian';

    return localizableString[key] || localizableString.Russian || '';
}

window.getLocalizableString = getLocalizableString;
window.setLocalizableString = setLocalizableString;
window.makeLocalizableString = makeLocalizableString;
window.changeLocalizableNameVisibility = changeLocalizableNameVisibility;
window.setLocalizableLabelString = setLocalizableLabelString;
window.getLocalizableStringErrors = getLocalizableStringErrors;
window.getLocalizedValue = getLocalizedValue;